import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClientProvider, useQuery } from '@tanstack/react-query';
import { ThemeProvider, CssBaseline, Box, CircularProgress, Typography } from '@mui/material';

// Componentes
import AppToolbar from './components/AppToolbar';
import AppDrawer from './components/AppDrawer';

// Configurações
import theme from './theme/theme';
import { queryClient } from './services/api';
import { menuService, MenuItem, MenuData } from './services/menu';

// Páginas
import Dashboard from './pages/Dashboard';
import Login from './pages/Login';
import Cargos from './pages/Cargos';
import CargoNovo from './pages/CargoNovo';
import CargoEditar from './pages/CargoEditar';
import CodigoBarras from './pages/CodigoBarras';
import CodigoBarrasNovo from './pages/CodigoBarrasNovo';
import CodigoBarrasEditar from './pages/CodigoBarrasEditar';
import Linhas from './pages/Linhas';
import LinhaNovo from './pages/LinhaNovo';
import LinhaEditar from './pages/LinhaEditar';
import Marcas from './pages/Marcas';
import MarcaNovo from './pages/MarcaNovo';
import MarcaEditar from './pages/MarcaEditar';
import Pracas from './pages/Pracas';
import PracaNovo from './pages/PracaNovo';
import PracaEditar from './pages/PracaEditar';
import PracaVisualizar from './pages/PracaVisualizar';
import Produtos from './pages/Produtos';
import ProdutoNovo from './pages/ProdutoNovo';
import ProdutoEditar from './pages/ProdutoEditar';
import ProdutoVisualizar from './pages/ProdutoVisualizar';
import ProdutoUnificar from './pages/ProdutoUnificar';
import Funcionarios from './pages/Funcionarios';
import FuncionarioNovo from './pages/FuncionarioNovo';
import FuncionarioEditar from './pages/FuncionarioEditar';
import FuncionarioVisualizar from './pages/FuncionarioVisualizar';
import FuncionarioRedirect from './pages/FuncionarioRedirect';
import Vendedores from './pages/Vendedores';
import VendedorNovo from './pages/VendedorNovo';
import VendedorEditar from './pages/VendedorEditar';
import VendedorVisualizar from './pages/VendedorVisualizar';
import VendedorUnificar from './pages/VendedorUnificar';
import Usuarios from './pages/Usuarios';
import UsuarioNovo from './pages/UsuarioNovo';
import UsuarioEditar from './pages/UsuarioEditar';
import UsuarioVisualizar from './pages/UsuarioVisualizar';
import Fornecedores from './pages/Fornecedores';
import FornecedorNovo from './pages/FornecedorNovo';
import FornecedorEditar from './pages/FornecedorEditar';
import Pedidos from './pages/Pedidos';
import PedidoNovo from './pages/PedidoNovo';
import PedidoEditar from './pages/PedidoEditar';
import PedidoVisualizar from './pages/PedidoVisualizar';
import Entradas from './pages/Entradas';
import EntradaNova from './pages/EntradaNova';
import EntradaVisualizar from './pages/EntradaVisualizar';
import PerfisAcesso from './pages/PerfisAcesso';
import PerfilAcessoNovo from './pages/PerfilAcessoNovo';
import PerfilAcessoEditar from './pages/PerfilAcessoEditar';
import PerfilAcessoVisualizar from './pages/PerfilAcessoVisualizar';
import Saidas from './pages/Saidas';
import SaidaNova from './pages/SaidaNova';
import SaidaVisualizar from './pages/SaidaVisualizar';
import SaidaRetorno from './pages/SaidaRetorno';
import SaidaRetornoVisualizar from './pages/SaidaRetornoVisualizar';
import SaidaAdicionarProdutos from './pages/SaidaAdicionarProdutos';
import SaidaAlterarDatas from './pages/SaidaAlterarDatas';
import SaidaUnificar from './pages/SaidaUnificar';
import Romaneio from './pages/Romaneio';
import RomaneioNovo from './pages/RomaneioNovo';
import RomaneioEditar from './pages/RomaneioEditar';
import RomaneioVisualizar from './pages/RomaneioVisualizar';
import RomaneioAlterarDatas from './pages/RomaneioAlterarDatas';
import RomaneioConferencia from './pages/RomaneioConferencia';
import RomaneioConferenciaEditar from './pages/RomaneioConferenciaEditar';
import ConferenciaRomaneioSaida from './pages/ConferenciaRomaneioSaida';
import ConferenciaRomaneioSaidaEditar from './pages/ConferenciaRomaneioSaidaEditar';
import AcertoPromotorVendas from './pages/AcertoPromotorVendas';
import NotFound from './pages/NotFound';
import Relatorios from './pages/Relatorios';
import RelatorioEstoque from './pages/RelatorioEstoque';
import RelatorioSaidaPorPracaDataRetorno from './pages/RelatorioSaidaPorPracaDataRetorno';
import RelatorioDiferencaRomaneioSaidas from './pages/RelatorioDiferencaRomaneioSaidas';
import RelatorioItensNaoVendidosRomaneio from './pages/RelatorioItensNaoVendidosRomaneio';
import RelatorioItensNaoFicaramSaida from './pages/RelatorioItensNaoFicaramSaida';
import RelatorioVendasPorPeriodoReferencia from './pages/RelatorioVendasPorPeriodoReferencia';
import RelatorioVendasPorPeriodoLinha from './pages/RelatorioVendasPorPeriodoLinha';
import RelatorioCobrancaPorPraca from './pages/RelatorioCobrancaPorPraca';
import Sacola from './pages/Sacola';
import SacolaCadastro from './pages/SacolaCadastro';
import SacolaVisualizar from './pages/SacolaVisualizar';
import Sincronizacoes from './pages/Sincronizacoes';
import SincronizacaoVisualizarStatus from './pages/SincronizacaoVisualizarStatus';

const App: React.FC = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);

  // Verifica se o usuário está autenticado
  useEffect(() => {
    const token = localStorage.getItem('setKeyUser');
    const isAuth = Boolean(token && token !== 'null' && token !== '');
    setIsAuthenticated(isAuth);
  }, []);

  // Escuta mudanças no localStorage (para logout em outras abas)
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'setKeyUser') {
        const token = e.newValue;
        setIsAuthenticated(Boolean(token && token !== 'null' && token !== ''));
      }
    };

    // Escuta evento customizado para login na mesma aba
    const handleLoginSuccess = () => {
      const token = localStorage.getItem('setKeyUser');
      const isAuth = Boolean(token && token !== 'null' && token !== '');
      setIsAuthenticated(isAuth);
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('loginSuccess', handleLoginSuccess);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('loginSuccess', handleLoginSuccess);
    };
  }, []);

  const handleDrawerToggle = () => {
    setDrawerOpen(!drawerOpen);
  };

  const handleDrawerClose = () => {
    setDrawerOpen(false);
  };

  // Componente para usuários autenticados (com menu)
  const AuthenticatedContent = () => {
    // Verificar novamente se está autenticado (double check)
    const token = localStorage.getItem('setKeyUser');
    if (!token || token === 'null' || token === '') {
      console.log('No token found in AuthenticatedContent, redirecting to login');
      return <Navigate to="/login" replace />;
    }

    // Carregar menu do usuário
    const { data: menuData, isLoading: menuLoading, error: menuError } = useQuery<MenuData>({
      queryKey: ['menu'],
      queryFn: menuService.getMenu,
      retry: 0, // Não tentar novamente para evitar loops
    });

    // Se houver erro no menu (provavelmente 401), limpar token e redirecionar
    if (menuError) {
      console.error('Menu error:', menuError);
      localStorage.removeItem('setKeyUser');
      return <Navigate to="/login" replace />;
    }

    // Converter menu items da API para o formato esperado
    const menuItems: MenuItem[] = menuData?.map((item: MenuItem) => ({
      ...item,
      icon: item.icon,
    })) || [];

    // Se estiver carregando menu
    if (menuLoading) {
      return (
        <Box 
          sx={{ 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center', 
            height: '100vh',
            backgroundColor: 'background.default'
          }}
        >
          <CircularProgress color="primary" />
        </Box>
      );
    }

    // Layout completo quando autenticado e menu carregado
    return (
      <Box sx={{ display: 'flex' }}>
        {/* Toolbar */}
        <AppToolbar
          onDrawerToggle={handleDrawerToggle}
          title="By Lust Lingerie"
        />

        {/* Drawer */}
        <AppDrawer
          open={drawerOpen}
          onClose={handleDrawerClose}
          menuItems={menuItems}
        />

        {/* Conteúdo Principal */}
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            p: 3,
            width: { sm: `calc(100% - 260px)` },
            mt: 8,
          }}
        >
          <Routes>
            <Route path="/" element={<Dashboard />} />
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/cargos" element={<Cargos />} />
            <Route path="/Cargo" element={<Cargos />} />
            <Route path="/cargos/novo" element={<CargoNovo />} />
            <Route path="/Cargo/novo" element={<CargoNovo />} />
            <Route path="/cargos/editar/:id" element={<CargoEditar />} />
            <Route path="/Cargo/editar/:id" element={<CargoEditar />} />
            <Route path="/codigoBarras" element={<CodigoBarras />} />
            <Route path="/codigoBarras/novo" element={<CodigoBarrasNovo />} />
            <Route path="/codigoBarras/editar/:id" element={<CodigoBarrasEditar />} />
            <Route path="/linhas" element={<Linhas />} />
            <Route path="/Linha" element={<Linhas />} />
            <Route path="/linhas/novo" element={<LinhaNovo />} />
            <Route path="/Linha/novo" element={<LinhaNovo />} />
            <Route path="/linhas/editar/:id" element={<LinhaEditar />} />
            <Route path="/Linha/editar/:id" element={<LinhaEditar />} />
            <Route path="/marcas" element={<Marcas />} />
            <Route path="/Marca" element={<Marcas />} />
            <Route path="/marcas/novo" element={<MarcaNovo />} />
            <Route path="/Marca/novo" element={<MarcaNovo />} />
            <Route path="/marcas/editar/:id" element={<MarcaEditar />} />
            <Route path="/Marca/editar/:id" element={<MarcaEditar />} />
            <Route path="/pracas" element={<Pracas />} />
            <Route path="/Praca" element={<Pracas />} />
            <Route path="/pracas/novo" element={<PracaNovo />} />
            <Route path="/Praca/novo" element={<PracaNovo />} />
            <Route path="/pracas/editar/:id" element={<PracaEditar />} />
            <Route path="/Praca/editar/:id" element={<PracaEditar />} />
            <Route path="/pracas/visualizar/:id" element={<PracaVisualizar />} />
            <Route path="/Praca/visualizar/:id" element={<PracaVisualizar />} />
            <Route path="/clientes" element={<div>Clientes</div>} />
            <Route path="/produtos" element={<Produtos />} />
            <Route path="/Produto" element={<Produtos />} />
            <Route path="/produtos/novo" element={<ProdutoNovo />} />
            <Route path="/Produto/novo" element={<ProdutoNovo />} />
            <Route path="/produtos/editar/:id" element={<ProdutoEditar />} />
            <Route path="/Produto/editar/:id" element={<ProdutoEditar />} />
            <Route path="/produtos/visualizar/:id" element={<ProdutoVisualizar />} />
            <Route path="/Produto/visualizar/:id" element={<ProdutoVisualizar />} />
            <Route path="/produtos/unificar/:id" element={<ProdutoUnificar />} />
            <Route path="/Produto/unificar/:id" element={<ProdutoUnificar />} />
            <Route path="/funcionarios" element={<Funcionarios />} />
            <Route path="/Funcionarios" element={<Funcionarios />} />
            <Route path="/Funcionario" element={<FuncionarioRedirect />} />
            <Route path="/funcionarios/novo" element={<FuncionarioNovo />} />
            <Route path="/Funcionarios/novo" element={<FuncionarioNovo />} />
            <Route path="/funcionarios/editar/:id" element={<FuncionarioEditar />} />
            <Route path="/Funcionarios/editar/:id" element={<FuncionarioEditar />} />
            <Route path="/funcionarios/visualizar/:id" element={<FuncionarioVisualizar />} />
            <Route path="/Funcionarios/visualizar/:id" element={<FuncionarioVisualizar />} />
            <Route path="/vendedores" element={<Vendedores />} />
            <Route path="/Vendedores" element={<Vendedores />} />
            <Route path="/vendedores/novo" element={<VendedorNovo />} />
            <Route path="/Vendedores/novo" element={<VendedorNovo />} />
            <Route path="/vendedores/editar/:id" element={<VendedorEditar />} />
            <Route path="/Vendedores/editar/:id" element={<VendedorEditar />} />
            <Route path="/vendedores/visualizar/:id" element={<VendedorVisualizar />} />
            <Route path="/Vendedores/visualizar/:id" element={<VendedorVisualizar />} />
            <Route path="/vendedores/unificar/:id" element={<VendedorUnificar />} />
            <Route path="/Vendedores/unificar/:id" element={<VendedorUnificar />} />
            <Route path="/usuarios" element={<Usuarios />} />
            <Route path="/Usuarios" element={<Usuarios />} />
            <Route path="/usuarios/novo" element={<UsuarioNovo />} />
            <Route path="/Usuarios/novo" element={<UsuarioNovo />} />
            <Route path="/usuarios/editar/:id" element={<UsuarioEditar />} />
            <Route path="/Usuarios/editar/:id" element={<UsuarioEditar />} />
            <Route path="/usuarios/visualizar/:id" element={<UsuarioVisualizar />} />
            <Route path="/Usuarios/visualizar/:id" element={<UsuarioVisualizar />} />
            <Route path="/fornecedores" element={<Fornecedores />} />
            <Route path="/Fornecedor" element={<Fornecedores />} />
            <Route path="/fornecedores/novo" element={<FornecedorNovo />} />
            <Route path="/Fornecedor/novo" element={<FornecedorNovo />} />
            <Route path="/fornecedores/editar/:id" element={<FornecedorEditar />} />
            <Route path="/Fornecedor/editar/:id" element={<FornecedorEditar />} />
            <Route path="/pedidos" element={<Pedidos />} />
            <Route path="/Pedido" element={<Pedidos />} />
            <Route path="/pedidos/novo" element={<PedidoNovo />} />
            <Route path="/Pedido/novo" element={<PedidoNovo />} />
            <Route path="/pedidos/editar/:id" element={<PedidoEditar />} />
            <Route path="/Pedido/editar/:id" element={<PedidoEditar />} />
            <Route path="/pedidos/visualizar/:id" element={<PedidoVisualizar />} />
            <Route path="/Pedido/visualizar/:id" element={<PedidoVisualizar />} />
            <Route path="/entradas" element={<Entradas />} />
            <Route path="/Entrada" element={<Entradas />} />
            <Route path="/entradas/novo" element={<EntradaNova />} />
            <Route path="/Entrada/novo" element={<EntradaNova />} />
            <Route path="/entradas/visualizar/:id" element={<EntradaVisualizar />} />
            <Route path="/Entrada/visualizar/:id" element={<EntradaVisualizar />} />
            <Route path="/perfis-acesso" element={<PerfisAcesso />} />
            <Route path="/PerfisAcesso" element={<PerfisAcesso />} />
            <Route path="/perfis-acesso/novo" element={<PerfilAcessoNovo />} />
            <Route path="/PerfisAcesso/novo" element={<PerfilAcessoNovo />} />
            <Route path="/perfis-acesso/editar/:id" element={<PerfilAcessoEditar />} />
            <Route path="/PerfisAcesso/editar/:id" element={<PerfilAcessoEditar />} />
            <Route path="/perfis-acesso/visualizar/:id" element={<PerfilAcessoVisualizar />} />
            <Route path="/PerfisAcesso/visualizar/:id" element={<PerfilAcessoVisualizar />} />
            <Route path="/saidas" element={<Saidas />} />
            <Route path="/SaidaConsignada" element={<Saidas />} />
            <Route path="/saidas/novo" element={<SaidaNova />} />
            <Route path="/saidas/visualizar/:id" element={<SaidaVisualizar />} />
            <Route path="/saidas/retorno/visualizar/:id" element={<SaidaRetornoVisualizar />} />
            <Route path="/saidas/retorno/:id" element={<SaidaRetorno />} />
            <Route path="/saidas/adicionar-produtos/:id" element={<SaidaAdicionarProdutos />} />
            <Route path="/saidas/alterar-datas/:id" element={<SaidaAlterarDatas />} />
            <Route path="/saidas/unificar/:id" element={<SaidaUnificar />} />
            <Route path="/romaneio" element={<Romaneio />} />
            <Route path="/romaneio/novo" element={<RomaneioNovo />} />
            <Route path="/romaneio/editar/:id" element={<RomaneioEditar />} />
            <Route path="/romaneio/visualizar/:id" element={<RomaneioVisualizar />} />
            <Route path="/romaneio/alterar-datas/:id" element={<RomaneioAlterarDatas />} />
            <Route path="/romaneio-conferencia" element={<RomaneioConferencia />} />
            <Route path="/ConferenciaRomaneio" element={<RomaneioConferencia />} />
            <Route path="/romaneio-conferencia/editar/:id" element={<RomaneioConferenciaEditar />} />
            <Route path="/ConferenciaRomaneio/Editar/:id" element={<RomaneioConferenciaEditar />} />
            <Route path="/ConferenciaRomaneioSaida" element={<ConferenciaRomaneioSaida />} />
            <Route path="/ConferenciaRomaneioSaida/Editar/:id" element={<ConferenciaRomaneioSaidaEditar />} />
            <Route path="/AcertoPromotorVendas" element={<AcertoPromotorVendas />} />
            <Route path="/estoque" element={<div>Estoque</div>} />
            <Route path="/financeiro" element={<div>Financeiro</div>} />
            <Route path="/relatorios" element={<Relatorios />} />
            <Route path="/Relatorios" element={<Relatorios />} />
            <Route path="/configuracoes" element={<div>Configurações</div>} />
            <Route path="/Relatorios/estoque" element={<RelatorioEstoque />} />
            <Route path="/Relatorios/saida-por-praca-e-data-retorno" element={<RelatorioSaidaPorPracaDataRetorno />} />
            <Route path="/Relatorios/diferenca-romaneio-saidas" element={<RelatorioDiferencaRomaneioSaidas />} />
            <Route path="/Relatorios/itens-nao-vendidos-por-romaneio" element={<RelatorioItensNaoVendidosRomaneio />} />
            <Route path="/Relatorios/itens-nao-ficaram-em-saida" element={<RelatorioItensNaoFicaramSaida />} />
            <Route path="/Relatorios/Venda-por-periodo" element={<RelatorioVendasPorPeriodoReferencia />} />
            <Route path="/Relatorios/Venda-por-periodo-linha" element={<RelatorioVendasPorPeriodoLinha />} />
            <Route path="/Relatorios/Cobranca-por-praca" element={<RelatorioCobrancaPorPraca />} />
            <Route path="/Sacola" element={<Sacola />} />
            <Route path="/Sacola/Cadastro" element={<SacolaCadastro />} />
            <Route path="/Sacola/View/:id" element={<SacolaVisualizar />} />
            <Route path="/Sincronizacao" element={<Sincronizacoes />} />
            <Route path="/Sincronizacao/visualizar/:id" element={<SincronizacaoVisualizarStatus />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Box>
      </Box>
    );
  };

  // Renderiza o app baseado no estado de autenticação
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <QueryClientProvider client={queryClient}>
        <Router>
          <Routes>
            <Route 
              path="/login" 
              element={isAuthenticated ? <Navigate to="/dashboard" replace /> : <Login />} 
            />
            <Route 
              path="/*" 
              element={isAuthenticated ? <AuthenticatedContent /> : <Navigate to="/login" replace />} 
            />
          </Routes>
        </Router>
      </QueryClientProvider>
    </ThemeProvider>
  );
};

export default App;
